import { RouterProvider } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import { useMe } from "./lib/me";
import { AuthProvider } from "./lib/auth";
import { ImpersonationProvider } from "./lib/impersonation";
import { apiUrl } from "./lib/api";
import { router } from "./router";

export function App() {
  const { me, loading, refetch } = useMe();

  // Route guards read the latest `me` via this ref so navigation after
  // login/logout is always evaluated against fresh auth state.
  const meRef = useRef(me);
  meRef.current = me;

  const auth = useMemo(
    () => ({
      me,
      refetchMe: refetch,
      onLoggedOut: () => {
        // Full page nav to the marketing site, outside the SPA tree.
        window.location.href = "https://vereinfach.app/";
      },
    }),
    [me, refetch],
  );

  if (loading) {
    return <div className="min-h-full flex items-center justify-center text-slate-400">Lädt…</div>;
  }

  // Anonymous user landed in the SPA (e.g. clicked "Gratis starten" on the
  // Astro landing while logged out). Bounce them back to the marketing
  // site via a real page navigation — the React router has no route tree
  // for them, so mounting the RouterProvider would spin in a redirect
  // loop trying to throw them back to "/".
  if (!me?.user) {
    if (typeof window !== "undefined") {
      window.location.href = `${apiUrl("/auth/google/start")}`;
    }
    return (
      <div className="min-h-full flex items-center justify-center text-slate-400">
        Weiterleiten…
      </div>
    );
  }

  return (
    <AuthProvider value={auth}>
      <ImpersonationProvider>
        <RouterProvider router={router} context={{ getMe: () => meRef.current }} />
      </ImpersonationProvider>
    </AuthProvider>
  );
}
